/**
 * src/utils/pricing.ts — Customer-facing pricing constants and helpers.
 *
 * ⚡ PRICING V2 (locked 2026-07-17 — Luke + Ali "Model F" hybrid): delivery/
 * retrieval fees are now PER VISIT with predefined booking windows:
 *   - Flat $15/crate/month storage (unchanged).
 *   - Delivery visit (up to 16 crates): flexible fortnight window $15;
 *     exact-day TIER $25 (replaces the base, never stacks). Published peak
 *     windows add $15/visit on either tier.
 *   - The 4-month waiver covers the BASE FLEXIBLE FEE only — never the peak
 *     adder, never exact-day. Any matured crate in the visit waives the base.
 *   - Free always: new-crate drop-offs, send-in pickups, empty-crate returns.
 *   - Fees/windows are served by GET /FeeSchedule (PricingConfig +
 *     DeliveryWindow tables); the ONE fee engine is
 *     resources/lib/deliveryFees.js — import it, never re-derive the matrix.
 *
 * The LEGACY per-crate helpers below (retrievalFeePerCrate, getTripCost,
 * isRetrievalFree…) encode the pre-v2 model and are @deprecated: they keep
 * the existing web screens compiling/rendering until those screens move to
 * the v2 scheduler + FeeSchedule (Phase 3). Do not add new callers.
 */

export {
  DEFAULT_PRICING_CONFIG,
  addCalendarMonthsYmd,
  computeDeliveryFee,
  isCrateMaturedBy,
  type DeliveryFeeBreakdown,
  type FeeLine,
  type PricingConfigValues,
} from '../../resources/lib/deliveryFees.js';

/** @deprecated pre-v2 constants — new code reads /FeeSchedule config (see DEFAULT_PRICING_CONFIG for fallbacks). */
export const PRICING = {
  /** Monthly storage fee per crate (USD whole dollars) */
  monthlyPerCrate: 15,
  /** Months of storage that waive the retrieval fee. */
  freeRetrievalAfterMonths: 4,
  /** Flat retrieval fee per crate, waived once stored ≥ freeRetrievalAfterMonths. */
  retrievalFeePerCrate: 15,
  /** Referral credit (each side — referrer and referee) in whole dollars */
  referralCredit: 15,
  /** Driver payout per crate per trip (internal, not shown to customer) */
  driverPayoutPerCrate: 8,
} as const;

/** Format a whole-dollar amount for display (e.g. 15 → "$15"). */
export function formatPrice(dollars: number): string {
  return `$${dollars}`;
}

/** Minimal shape needed to reason about a crate's retrieval eligibility. */
export type RetrievableCrate = { stored_at?: string | null };

/**
 * Add a number of CALENDAR months to a date, clamping to the last valid day
 * of the target month when the original day-of-month doesn't exist there.
 *
 *   Jun 22 + 4mo → Oct 22         (day exists)
 *   Oct 31 + 4mo → Feb 28 / 29    (Feb has no 31st → clamp, NOT roll to Mar 1)
 *   Aug 31 + 4mo → Dec 31         (Dec has a 31st → no clamp)
 *
 * Time-of-day is preserved. Matches Stripe's subscription-anchor behavior.
 */
export function addCalendarMonths(date: Date, months: number): Date {
  const day = date.getDate();
  const targetIndex = date.getMonth() + months;
  const targetYear = date.getFullYear() + Math.floor(targetIndex / 12);
  const targetMonth = ((targetIndex % 12) + 12) % 12;

  const result = new Date(date.getTime());
  // Move to the 1st first so changing the month can't overflow (e.g. setting
  // month to Feb while the date is the 31st would roll into March).
  result.setDate(1);
  result.setFullYear(targetYear, targetMonth, 1);
  const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  result.setDate(Math.min(day, lastDayOfTargetMonth));
  return result;
}

/**
 * @deprecated pre-v2 per-crate model — Phase 3 replaces callers with computeDeliveryFee + /FeeSchedule. The date a crate's retrieval fee is waived (free retrieval unlocks).
 * Returns null when the crate has no storage clock yet (never stored / legacy
 * record) — callers treat null as "fee applies, no unlock date to show".
 */
export function getFreeRetrievalDate(storedAt: string | null | undefined): Date | null {
  if (!storedAt) return null;
  const stored = new Date(storedAt);
  if (Number.isNaN(stored.getTime())) return null;
  return addCalendarMonths(stored, PRICING.freeRetrievalAfterMonths);
}

/**
 * @deprecated pre-v2 per-crate model — Phase 3 replaces callers with computeDeliveryFee + /FeeSchedule.
 * True once a crate has been stored long enough that its retrieval is free. */
export function isRetrievalFree(crate: RetrievableCrate, now: Date = new Date()): boolean {
  const freeDate = getFreeRetrievalDate(crate.stored_at);
  if (!freeDate) return false;
  return now.getTime() >= freeDate.getTime();
}

/**
 * @deprecated pre-v2 per-crate model — Phase 3 replaces callers with computeDeliveryFee + /FeeSchedule.
 * The retrieval fee for a single crate right now: $0 if earned-free, else $15. */
export function getCrateRetrievalCost(crate: RetrievableCrate, now: Date = new Date()): number {
  return isRetrievalFree(crate, now) ? 0 : PRICING.retrievalFeePerCrate;
}

export interface TripCost {
  /** Total retrieval fee for the trip, in whole dollars. */
  amount: number;
  /** Summary label, e.g. "Free retrieval" or "$30 retrieval fee". */
  label: string;
  /** How many crates in the trip retrieve free (earned). */
  freeCount: number;
  /** How many crates in the trip still carry the fee. */
  paidCount: number;
}

/**
 * @deprecated pre-v2 per-crate model — Phase 3 replaces callers with computeDeliveryFee + /FeeSchedule. Cost of bringing a set of crates home in one trip. Flat $15 per crate that
 * hasn't earned its free retrieval; $0 for crates that have. No bulk discount
 * (Option A, locked 2026-06-22).
 */
export function getTripCost(crates: RetrievableCrate[], now: Date = new Date()): TripCost {
  let amount = 0;
  let freeCount = 0;
  let paidCount = 0;
  for (const crate of crates) {
    const cost = getCrateRetrievalCost(crate, now);
    if (cost === 0) {
      freeCount += 1;
    } else {
      paidCount += 1;
      amount += cost;
    }
  }
  const label = amount === 0 ? 'Free retrieval' : `${formatPrice(amount)} retrieval fee`;
  return { amount, label, freeCount, paidCount };
}
